import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Grid, LinearProgress, MenuItem, Paper, Select, Snackbar, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Toolbar, Tooltip, Typography,
  useTheme, useMediaQuery, Stack
} from '@mui/material';
import { Assessment as AssessmentIcon, CheckCircle, Delete as DeleteIcon, Pending, Refresh as RefreshIcon, TrendingUp } from '@mui/icons-material';
import { academicAPI, assessmentAPI } from '../../services/api';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';
import TablePagination from '../common/TablePagination';

const extractCollection = (r) => {
  const p = r?.data !== undefined ? r.data : r;
  if (Array.isArray(p)) return p;
  if (Array.isArray(p?.data)) return p.data;
  if (Array.isArray(p?.data?.data)) return p.data.data;
  return [];
};
const extractPayload = (r) => (r?.data !== undefined ? r.data : r);
const getErrorMessage = (e, fallback = 'Operation failed') => e?.response?.data?.message || e?.response?.data?.error || fallback;
const getStatusColor = (s) => { const n = String(s || '').toLowerCase(); if (n === 'completed') return 'success'; if (n === 'in_progress' || n === 'pending') return 'warning'; return 'default'; };
const getSubjectName = (s) => s?.subject_name || s?.name || s?.title || '-';
const normalizePercentage = (v) => { const n = Number(v); return Number.isNaN(n) ? 0 : Math.min(100, Math.max(0, n)); };
const paginate = (arr, page, perPage) => arr.slice((page - 1) * perPage, page * perPage);
const buildPagination = (arr, page, perPage) => ({ current_page: page, last_page: Math.ceil(arr.length / perPage), per_page: perPage, total: arr.length });

const responsiveAutocompleteSx = { width: '100%', minWidth: { sm: 240, lg: 280 } };

const AssessmentReports = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [analytics, setAnalytics] = useState(null);
  const [progress, setProgress] = useState([]);
  const [classes, setClasses] = useState([]);
  const [terms, setTerms] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [academicYear, setAcademicYear] = useState(new Date().getFullYear());
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [progressPage, setProgressPage] = useState(1);
  const [progressPerPage, setProgressPerPage] = useState(15);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const analyticsStats = useMemo(() => analytics?.overall_statistics || analytics?.statistics || analytics?.summary || {}, [analytics]);

  const progressRows = useMemo(() => {
    const rows = [];
    progress.forEach((sg) => {
      const subjectLabel = getSubjectName(sg?.subject_paper?.subject);
      const assessments = Array.isArray(sg?.assessments) ? sg.assessments : [];
      assessments.forEach((a, i) => rows.push({ id: `${subjectLabel}-${a?.id || i}`, subjectLabel, assessment: a }));
      if (assessments.length === 0) rows.push({ id: `${subjectLabel}-empty`, subjectLabel, assessment: null });
    });
    return rows;
  }, [progress]);

  const fetchFilters = useCallback(async () => {
    setLoadingFilters(true);
    try {
      const [cr, tr] = await Promise.allSettled([academicAPI.getClasses(), academicAPI.getTerms()]);
      setClasses(cr.status === 'fulfilled' ? extractCollection(cr.value) : []);
      const fetchedTerms = tr.status === 'fulfilled' ? extractCollection(tr.value) : [];
      setTerms(fetchedTerms);
      const current = fetchedTerms.find((t) => t.is_current || t.current);
      if (current) { setSelectedTerm(current.id); setAcademicYear(current.year || current.academic_year || new Date().getFullYear()); }
    } catch {
      setSnackbar({ open: true, message: 'Failed to load filters', severity: 'error' });
    } finally {
      setLoadingFilters(false);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    setLoadingAnalytics(true);
    try {
      const params = { academic_year: academicYear, term_id: selectedTerm, ...(selectedClass && { class_id: selectedClass }) };
      const payload = extractPayload(await assessmentAPI.getAnalytics(params));
      setAnalytics(payload?.data || payload);
    } catch (e) {
      setAnalytics(null);
      setSnackbar({ open: true, message: getErrorMessage(e, 'Failed to load analytics'), severity: 'error' });
    } finally { setLoadingAnalytics(false); }
  }, [academicYear, selectedClass, selectedTerm]);

  const fetchProgress = useCallback(async () => {
    setLoadingProgress(true);
    try {
      const params = { academic_year: academicYear, term_id: selectedTerm, ...(selectedClass && { class_id: selectedClass }) };
      const payload = extractPayload(await assessmentAPI.getAssessmentProgress(params));
      const subjects = payload?.subjects || payload?.data?.subjects || payload?.assessment_progress || extractCollection(payload);
      setProgress(Array.isArray(subjects) ? subjects : []);
    } catch (e) {
      setProgress([]);
      setSnackbar({ open: true, message: getErrorMessage(e, 'Failed to load progress'), severity: 'error' });
    } finally { setLoadingProgress(false); }
  }, [academicYear, selectedClass, selectedTerm]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);

  useEffect(() => {
    if (selectedTerm && academicYear) { fetchAnalytics(); fetchProgress(); }
    else { setAnalytics(null); setProgress([]); }
  }, [academicYear, fetchAnalytics, fetchProgress, selectedTerm]);

  useEffect(() => { setProgressPage(1); setSelectedIds([]); }, [academicYear, selectedTerm, selectedClass]);

  const toggleRow = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const executeBulkAction = async () => {
    if (!bulkAction || selectedIds.length === 0) return;
    setBulkLoading(true);
    setConfirmOpen(false);
    try {
      if (bulkAction === 'delete') {
        await Promise.all(selectedIds.map((id) => assessmentAPI.deleteAssessmentConfiguration(id)));
        setSnackbar({ open: true, message: `${selectedIds.length} assessment(s) deleted`, severity: 'success' });
      } else {
        await assessmentAPI.bulkUpdateAssessmentConfigurations({ ids: selectedIds, status: bulkAction });
        setSnackbar({ open: true, message: `${selectedIds.length} assessment(s) updated to "${bulkAction}"`, severity: 'success' });
      }
      setSelectedIds([]);
      setBulkAction('');
      await fetchProgress();
    } catch (e) {
      setSnackbar({ open: true, message: getErrorMessage(e, 'Bulk action failed'), severity: 'error' });
    } finally { setBulkLoading(false); }
  };

  const pagedProgressRows = useMemo(() => paginate(progressRows, progressPage, progressPerPage), [progressRows, progressPage, progressPerPage]);
  const progressPagination = buildPagination(progressRows, progressPage, progressPerPage);

  const selectableRows = useMemo(() => pagedProgressRows.filter((r) => r.assessment?.id), [pagedProgressRows]);
  const allPageSelected = selectableRows.length > 0 && selectableRows.every((r) => selectedIds.includes(r.assessment.id));
  const somePageSelected = selectableRows.some((r) => selectedIds.includes(r.assessment.id));

  const toggleAll = () => {
    if (allPageSelected) setSelectedIds((prev) => prev.filter((id) => !selectableRows.map((r) => r.assessment.id).includes(id)));
    else setSelectedIds((prev) => [...new Set([...prev, ...selectableRows.map((r) => r.assessment.id)])]);
  };

  return (
    <Box>
      <ModulePageHeader
        title="Assessment Reports & Analytics"
        subtitle="Monitor completion and progress by subject"
        actions={[
          <Button key="refresh" variant="outlined" startIcon={<RefreshIcon />}
            onClick={async () => { await fetchFilters(); if (selectedTerm && academicYear) await Promise.all([fetchAnalytics(), fetchProgress()]); }}
            disabled={loadingFilters || loadingAnalytics || loadingProgress} fullWidth={isMobile}
          >Refresh</Button>
        ]}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth size="small" type="number" label="Academic Year"
                value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}
                inputProps={{ min: 2000, max: 2100 }} disabled={loadingFilters}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete fullWidth sx={responsiveAutocompleteSx} options={terms}
                value={terms.find((t) => String(t.id) === String(selectedTerm)) || null}
                onChange={(_, v) => setSelectedTerm(v?.id || '')}
                getOptionLabel={(o) => `${o.name || 'Term'} ${o.year || ''}`.trim()}
                isOptionEqualToValue={(o, v) => o.id === v.id} disabled={loadingFilters}
                renderInput={(p) => <TextField {...p} label="Term" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete fullWidth sx={responsiveAutocompleteSx} options={classes}
                value={classes.find((c) => String(c.id) === String(selectedClass)) || null}
                onChange={(_, v) => setSelectedClass(v?.id || '')}
                getOptionLabel={(o) => o.name || o.class_name || 'Class'}
                isOptionEqualToValue={(o, v) => o.id === v.id} disabled={loadingFilters}
                renderInput={(p) => <TextField {...p} label="Class (Optional)" size="small" />}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {!selectedTerm && (
        <Card sx={{ mb: 3 }}>
          <CardContent><Typography color="text.secondary">Select a term to view analytics and progress.</Typography></CardContent>
        </Card>
      )}

      {selectedTerm && (
        <Grid container spacing={3} mb={3}>
          {[
            { icon: <AssessmentIcon color="primary" />, label: 'Total Assessments', value: analyticsStats.total_assessments || 0 },
            { icon: <CheckCircle color="success" />, label: 'Completed', value: analyticsStats.completed_assessments || 0 },
            { icon: <Pending color="warning" />, label: 'Pending', value: analyticsStats.pending_assessments || 0 },
            { icon: <TrendingUp color="info" />, label: 'Completion Rate', value: `${normalizePercentage(analyticsStats.average_completion_rate)}%` },
          ].map(({ icon, label, value }) => (
            <Grid item xs={12} md={3} key={label}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>{icon}<Typography variant="h6" ml={1}>{label}</Typography></Box>
                  {loadingAnalytics ? <CircularProgress size={24} /> : <Typography variant="h4">{value}</Typography>}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="h6">Assessment Progress by Subject</Typography>
            {selectedIds.length > 0 && (
              <Toolbar disableGutters sx={{ gap: 1, minHeight: 'unset !important' }}>
                <Typography variant="body2" color="text.secondary">{selectedIds.length} selected</Typography>
                <Select size="small" value={bulkAction} onChange={(e) => setBulkAction(e.target.value)}
                  displayEmpty sx={{ minWidth: 160 }} disabled={bulkLoading}
                >
                  <MenuItem value="" disabled>Bulk Action</MenuItem>
                  <MenuItem value="completed">Mark Completed</MenuItem>
                  <MenuItem value="pending">Mark Pending</MenuItem>
                  <MenuItem value="in_progress">Mark In Progress</MenuItem>
                  <MenuItem value="delete">Delete Selected</MenuItem>
                </Select>
                <Tooltip title="Apply">
                  <span>
                    <Button variant="contained" size="small" color={bulkAction === 'delete' ? 'error' : 'primary'}
                      disabled={!bulkAction || bulkLoading} onClick={() => setConfirmOpen(true)}
                      startIcon={bulkAction === 'delete' ? <DeleteIcon /> : undefined}
                    >{bulkLoading ? <CircularProgress size={16} /> : 'Apply'}</Button>
                  </span>
                </Tooltip>
              </Toolbar>
            )}
          </Box>
          {loadingProgress ? (
            <Box py={5} textAlign="center"><CircularProgress size={28} /></Box>
          ) : progressRows.length === 0 ? (
            <EmptyRecordsState message="No progress records found for the selected filters." />
          ) : isMobile ? (
            <>
              <ResponsiveRecordList items={pagedProgressRows} renderItem={(row) => {
                if (!row.assessment) return (
                  <Stack spacing={1}>
                    <Typography fontWeight={700}>{row.subjectLabel}</Typography>
                    <Typography variant="body2" color="text.secondary">No configured assessments</Typography>
                  </Stack>
                );
                const completion = normalizePercentage(row.assessment?.completion_percentage ?? row.assessment?.completion_rate ?? row.assessment?.progress_percentage);
                const sm = Number(row.assessment?.students_marked ?? row.assessment?.marked_students ?? 0);
                const sp = Number(row.assessment?.students_pending ?? row.assessment?.pending_students ?? 0);
                return (
                  <Stack spacing={1}>
                    <Typography fontWeight={700}>{row.subjectLabel}</Typography>
                    <Typography variant="body2" color="text.secondary">{row.assessment?.assessment_type?.assessment_name || '-'}</Typography>
                    <Typography variant="body2" color="text.secondary">Due: {row.assessment?.due_date ? String(row.assessment.due_date).slice(0, 10) : '-'}</Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LinearProgress variant="determinate" value={completion} sx={{ flexGrow: 1, height: 8, borderRadius: 1 }} />
                      <Typography variant="body2">{completion}%</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">{sm}/{sm + sp} students</Typography>
                      <Chip label={String(row.assessment?.status || 'unknown').replaceAll('_', ' ')} color={getStatusColor(row.assessment?.status)} size="small" sx={{ textTransform: 'capitalize' }} />
                    </Box>
                  </Stack>
                );
              }} />
              <TablePagination pagination={progressPagination} onPageChange={setProgressPage} onPageSizeChange={(s) => { setProgressPerPage(s); setProgressPage(1); }} isMobile />
            </>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 960 }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox indeterminate={somePageSelected && !allPageSelected} checked={allPageSelected} onChange={toggleAll} disabled={selectableRows.length === 0} />
                    </TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Assessment Type</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedProgressRows.map((row, idx) => {
                    if (!row.assessment) return (
                      <TableRow key={row.id}>
                        <TableCell padding="checkbox" />
                        <TableCell><strong>{row.subjectLabel}</strong></TableCell>
                        <TableCell colSpan={4} sx={{ color: 'text.secondary' }}>No configured assessments</TableCell>
                      </TableRow>
                    );
                    const isFirst = idx === 0 || pagedProgressRows[idx - 1]?.subjectLabel !== row.subjectLabel;
                    const span = pagedProgressRows.filter((r) => r.subjectLabel === row.subjectLabel && r.assessment).length;
                    const completion = normalizePercentage(row.assessment?.completion_percentage ?? row.assessment?.completion_rate ?? row.assessment?.progress_percentage);
                    const sm = Number(row.assessment?.students_marked ?? row.assessment?.marked_students ?? 0);
                    const sp = Number(row.assessment?.students_pending ?? row.assessment?.pending_students ?? 0);
                    const isSelected = selectedIds.includes(row.assessment.id);
                    return (
                      <TableRow key={row.id} selected={isSelected} hover>
                        <TableCell padding="checkbox">
                          <Checkbox checked={isSelected} onChange={() => toggleRow(row.assessment.id)} />
                        </TableCell>
                        {isFirst && <TableCell rowSpan={span}><strong>{row.subjectLabel}</strong></TableCell>}
                        <TableCell>{row.assessment?.assessment_type?.assessment_name || '-'}</TableCell>
                        <TableCell>{row.assessment?.due_date ? String(row.assessment.due_date).slice(0, 10) : '-'}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <LinearProgress variant="determinate" value={completion} sx={{ flexGrow: 1, height: 8, borderRadius: 1 }} />
                            <Typography variant="body2">{completion}%</Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">{sm}/{sm + sp} students</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={String(row.assessment?.status || 'unknown').replaceAll('_', ' ')} color={getStatusColor(row.assessment?.status)} size="small" sx={{ textTransform: 'capitalize' }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination pagination={progressPagination} onPageChange={setProgressPage} onPageSizeChange={(s) => { setProgressPerPage(s); setProgressPage(1); }} isMobile={isMobile} />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{bulkAction === 'delete' ? 'Confirm Delete' : 'Confirm Update'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {bulkAction === 'delete'
              ? `Permanently delete ${selectedIds.length} assessment(s)? This cannot be undone.`
              : `Update ${selectedIds.length} assessment(s) to "${String(bulkAction).replaceAll('_', ' ')}"?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={executeBulkAction} color={bulkAction === 'delete' ? 'error' : 'primary'} variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AssessmentReports;
